<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useZuwadAssistantStore } from 'dashboard/stores/zuwadAssistant';
import AssistantChat from './components/AssistantChat.vue';
import AssistantThreadList from './components/AssistantThreadList.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useZuwadAssistantStore();

const accountId = computed(() => Number(route.params.accountId));
const threadId = computed(() =>
  route.params.threadId ? Number(route.params.threadId) : null
);

const goTo = id =>
  router.push({
    name: id ? 'zuwad_assistant_thread' : 'zuwad_assistant',
    params: { accountId: accountId.value, ...(id ? { threadId: id } : {}) },
  });

onMounted(async () => {
  if (!store.loaded || store.accountId !== accountId.value)
    await store.load(accountId.value);
  await store.open(threadId.value);
});
watch(threadId, id => store.open(id));
// A first question from the empty page creates a thread: put it in the URL.
watch(
  () => store.activeThreadId,
  id => {
    if (id && id !== threadId.value) goTo(id);
  }
);

// Browser notifications are asked for on a click, never on page load.
const notifyPermission = ref(
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
);
const enableNotifications = async () => {
  notifyPermission.value = await Notification.requestPermission();
};

const newChat = async () => {
  const thread = await store.newThread();
  goTo(thread.id);
};
const rename = async thread => {
  // eslint-disable-next-line no-alert
  const title = window.prompt(t('ZUWAD_ASSISTANT.RENAME_PROMPT'), thread.title);
  if (title && title.trim()) await store.rename(thread.id, title.trim());
};
const remove = async thread => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('ZUWAD_ASSISTANT.DELETE_CONFIRM'))) return;
  await store.remove(thread.id);
  if (threadId.value === thread.id) goTo(null);
};
</script>

<template>
  <section class="flex w-full h-full min-h-0 overflow-hidden bg-n-surface-1">
    <aside
      class="flex flex-col w-80 shrink-0 border-e border-n-weak bg-n-solid-1 max-md:hidden"
    >
      <div
        class="flex items-center justify-between gap-2 px-3 h-14 border-b border-n-weak"
      >
        <h1
          class="flex items-center gap-2.5 text-base font-semibold text-n-slate-12"
        >
          <span
            class="flex items-center justify-center rounded-lg size-8 bg-n-brand text-n-gold shadow-sm"
          >
            <span class="i-woot-zuwad-ai size-[18px]" />
          </span>
          {{ t('ZUWAD_ASSISTANT.TITLE') }}
        </h1>
        <Button
          icon="i-lucide-plus"
          size="sm"
          :label="t('ZUWAD_ASSISTANT.NEW_CHAT')"
          @click="newChat"
        />
      </div>
      <AssistantThreadList
        :active-id="threadId"
        @open="goTo"
        @rename="rename"
        @remove="remove"
      />
      <button
        v-if="notifyPermission === 'default'"
        type="button"
        class="flex items-center gap-2 m-2 px-3 py-2 text-xs text-start rounded-lg bg-n-alpha-1 text-n-slate-11 hover:text-n-slate-12"
        @click="enableNotifications"
      >
        <span class="i-lucide-bell-ring size-4 shrink-0 text-n-brand" />
        {{ t('ZUWAD_ASSISTANT.ENABLE_NOTIFICATIONS') }}
      </button>
    </aside>
    <div class="flex flex-col flex-1 min-w-0 min-h-0">
      <header
        class="flex items-center gap-2.5 px-4 h-14 border-b border-n-weak md:hidden"
      >
        <span
          class="flex items-center justify-center rounded-lg size-8 bg-n-brand text-n-gold shadow-sm"
        >
          <span class="i-woot-zuwad-ai size-[18px]" />
        </span>
        <span class="flex-1 font-medium">{{ t('ZUWAD_ASSISTANT.TITLE') }}</span>
        <Button icon="i-lucide-plus" size="sm" @click="newChat" />
      </header>
      <p
        v-if="store.activeThread && !store.activeThread.mine"
        class="flex items-center gap-2 px-4 py-2 text-xs border-b border-n-weak text-n-slate-11 bg-n-alpha-1"
      >
        <span class="i-lucide-users size-3.5 shrink-0" />
        <span dir="auto">{{
          t('ZUWAD_ASSISTANT.SHARED_CHAT', {
            name: store.activeThread.owner?.name || '',
          })
        }}</span>
      </p>
      <div class="flex-1 min-h-0 w-full max-w-4xl mx-auto">
        <AssistantChat />
      </div>
    </div>
  </section>
</template>
